import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import moment from 'moment'
import { get } from 'lodash'

import { resolvePaymentIcon, SQUARE_ICONS } from '../../utils/icons'
import { createOrderParams } from '../../utils/orderUtils'

import Button from '../Button'

import { getTenderInfo, totalDisplay } from '../../utils/orderDisplay'
import { INTERNAL_ORDER_STATES, STADIUM_ORDER_STATES, isTabbed } from '../../utils/orderStates'
import { getOrderIsApprovedOffline, getOrderIsUnsyncable, orderDate } from '../../utils/orderUtils'

import styles from './OrderRow.module.scss'
import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'
import { ButtonBase } from '@material-ui/core'
import FailedIcon from '../icons/FailedIcon'
import ReturnIcon from '../icons/ReturnIcon'
import AbortedIcon from '../icons/AbortedIcon'
import ApprovedIcon from '../icons/ApprovedIcon'
import DefectiveIcon from '../icons/DefectiveIcon'
import { useSelector } from 'react-redux'
import { getReopenedTabPayments } from '../../VNTenders/Selectors'
import { isE700Device } from '../../VNAndroidSDK/Utils'

// List of possible display icons and text used for getStatusDisplay
// Icons and text should be based on platform order state with the exception
// of "unsyncable" which isn't a true order state
const statusDisplays = {
  [STADIUM_ORDER_STATES.CANCELLED]: {
    icon: <FailedIcon className={styles.statusIcon} />,
    text: 'Cancelled',
  },
  [STADIUM_ORDER_STATES.REFUNDED]: {
    icon: <ReturnIcon className={styles.statusIcon} />,
    text: 'Refunded',
  },
  [STADIUM_ORDER_STATES.PARTIALLY_REFUNDED]: {
    icon: <ReturnIcon className={styles.statusIcon} />,
    text: 'Partially Refunded',
  },
  [STADIUM_ORDER_STATES.SUBMISSION_FAILED]: {
    icon: <FailedIcon className={styles.statusIcon} />,
    text: 'Aborted',
  },
  [STADIUM_ORDER_STATES.SUBMITTED]: {
    icon: <AbortedIcon className={styles.statusIcon} />,
    text: 'Aborted',
  },
  [STADIUM_ORDER_STATES.COMPLETED]: {
    icon: <ApprovedIcon className={styles.statusIcon} />,
    text: 'Approved',
  },
  [STADIUM_ORDER_STATES.COMPLETION_PENDING]: {
    icon: <ApprovedIcon className={styles.statusIcon} />,
    text: 'Approved',
  },
  [STADIUM_ORDER_STATES.PROCESSING]: {
    icon: <ApprovedIcon className={styles.statusIcon} />,
    text: 'Approved',
  },
  [STADIUM_ORDER_STATES.APPROVED_OFFLINE]: {
    icon: <ApprovedIcon className={styles.statusIcon} />,
    text: 'Approved',
  },
  [STADIUM_ORDER_STATES.TABBED]: {
    icon: <ApprovedIcon className={styles.statusIcon} />,
    text: 'Tabbed',
  },
  [STADIUM_ORDER_STATES.TABBED_OFFLINE]: {
    icon: <ApprovedIcon className={styles.statusIcon} />,
    text: 'Tabbed Offline',
  },
  [STADIUM_ORDER_STATES.AUTHORIZATION_FAILED]: {
    icon: <DefectiveIcon className={styles.statusIcon} />,
    text: 'Auth Failed',
  },
  unsyncable: {
    icon: <DefectiveIcon className={styles.statusIcon} />,
    text: 'Defective',
  },
}

// returns icon and text based on the current order state
const getStatusDisplay = (orderStatus) => {
  const icon = statusDisplays[orderStatus]?.icon
  const text = statusDisplays[orderStatus]?.text
  return (
    <div className={styles.status}>
      <span> {icon} </span>
      <span className={styles.description}>{text}</span>
    </div>
  )
}

const displayCreated = (created, isMobileViewPort = false) => {
  const date = moment(created).format(isMobileViewPort ? 'M/D/YY' : 'M/D')
  const time = moment(created).format('h:mm a')

  return date + ', ' + time
}

const OrderRow = ({ onSelect, order, isKiosk, hasLocations, className }) => {
  const authFailed = order?.isDeclined || order?.orderState === INTERNAL_ORDER_STATES.FAILED
  const errorClass = authFailed ? styles.error : null
  const isDefective = useMemo(() => {
    if (order?.state === STADIUM_ORDER_STATES.AUTHORIZATION_FAILED) {
      return true
    }

    return order?.unsyncable
  }, [order?.state, order?.unsyncable])
  const unsyncableClass =
    isDefective || createOrderParams(order)?.state === STADIUM_ORDER_STATES.SUBMITTED ? styles.unsyncable : null
  const redButtonClass = isDefective ? styles.errorButton : null
  const classNames = cn(styles.orderRow, errorClass, unsyncableClass, className)
  const reopenedTabPayments = useSelector((state) => getReopenedTabPayments(state, order.uuid))
  const tenderResponse = getTenderInfo(order, reopenedTabPayments)
  const iconType = tenderResponse?.cardIssuer ?? tenderResponse.displayText
  const isSquareIcon = SQUARE_ICONS.includes(iconType)
  const icon = React.cloneElement(resolvePaymentIcon(iconType), { className: cn(styles.icon, { [styles.square]: isSquareIcon }) })
  const isMobileViewPort = useGetIsMobileScreen()

  const orderNumber = (order) => {
    // Favor the order number that was actually created in the DB, if it exists.
    return createOrderParams(order)?.orderNumber ?? get(order, 'orderNumber', '-')
  }

  // TODO(mkramerl): Make this dynamic so text value can reflec the sync button being pressed in the detail.
  let initialSyncStatusText = ''

  if (getOrderIsUnsyncable(order)) {
    initialSyncStatusText = getStatusDisplay('unsyncable')
  } else if (order.isSyncing) {
    initialSyncStatusText = 'syncing'
  } else if (isTabbed(order) && order.orderState === INTERNAL_ORDER_STATES.CLOSED) {
    initialSyncStatusText = getStatusDisplay('completion_pending')
  } else if (getOrderIsApprovedOffline(order) && order?.state !== STADIUM_ORDER_STATES.CANCELLED) {
    initialSyncStatusText = getStatusDisplay('approved_offline')
  } else {
    initialSyncStatusText = getStatusDisplay((order?.state || createOrderParams(order)?.state) ?? 'completed')
  }

  const tabName = order.tabName ?? order.userName
  const mobileText = isMobileViewPort ? ' • ' : ' '
  const orderRowDisplayText =
    tabName && !isMobileViewPort ? `${tenderResponse?.displayText} • ${tabName}` : tenderResponse?.displayText
  const location = order?.userNotes?.split('\nNote:')?.[0]

  if (!isMobileViewPort) {
    return (
      <>
        <div className={classNames} onClick={() => onSelect(order.uuid)}>
          <div className={styles.centerContainer}>
            {icon}
            {!isKiosk && (
              <div className={styles.tenderNameContainer}>
                <span className={styles.tenderName}>{orderRowDisplayText}</span>
                <span className={styles.tenderName}>{order.userNotes}</span>
              </div>
            )}
          </div>
          <div>
            {mobileText}
            {totalDisplay(order)}
          </div>
          <div>{orderNumber(order)}</div>
          <div>{displayCreated(orderDate(order))}</div>
          <div>{initialSyncStatusText}</div>
          <div>
            <Button useMui className={cn(styles.detailsButton, redButtonClass)}>
              Details
            </Button>
          </div>
        </div>
      </>
    )
  }

  return (
    <ButtonBase disableRipple={isE700Device()} component="div" className={styles.container} onClick={() => onSelect(order.uuid)}>
      <div className={styles.orderRow}>
        {hasLocations && !location && <div className={styles.spacer}></div>}
        <div className={styles.centerContainer}>
          <span className={styles.mobileIcon}>{icon}</span>
          <span className={styles.mobileTabName}>{tabName}</span>
        </div>
        {!!location && <span className={styles.userNotes}>{location}</span>}
      </div>

      <div className={classNames}>
        <div className={styles.subrow}>
          <div className={styles.subrowContainer}>
            <div className={styles.centerContainer}>
              {!isKiosk && <span className={styles.tenderName}>{orderRowDisplayText}</span>}
            </div>
            <div className={styles.subrowDynamic}>
              {mobileText}
              {totalDisplay(order)}
            </div>
            <div>{orderNumber(order)}</div>
          </div>
          <div className={styles.subrowContainer}>
            <div>{displayCreated(orderDate(order), true)}</div>
            <div>{initialSyncStatusText}</div>
          </div>
          {hasLocations && !location && <div className={styles.spacer}></div>}
        </div>
      </div>
    </ButtonBase>
  )
}

OrderRow.defaultProps = {
  order: {},
}

OrderRow.propTypes = {
  onSelect: PropTypes.func,
  order: PropTypes.object,
}

export default OrderRow
